"use client";

/**
 * Per-row actions kebab for the sequences list table.
 *
 * Actions:
 *   - Clone sequence (always) — opens the create wizard in "clone" mode,
 *     pre-filled from the source sequence; the backend copies the steps.
 *   - Archive / Unarchive — soft-hide the sequence (or restore it). Archiving
 *     blocks new enrollments but leaves in-flight enrollments running.
 *
 * The trigger + items stop row-click propagation so opening the menu or an
 * action doesn't also navigate into the sequence detail page.
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { ActionMenuKit } from "@/components/ui/action-menu-kit";
import {
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import {
  getListSequencesQueryKey,
  getSequence,
  useArchiveSequence,
  useUnarchiveSequence,
} from "@/lib/api/generated";

import { invalidateProjectSequences } from "@/features/sequences";
import { sequenceToWizardPrefill } from "../../lib/clone";
import { useSequenceWizardStore } from "../../store/wizard-store";
import type { SequenceListItem } from "../../hooks/use-sequences";

interface RowActionsMenuProps {
  seq: SequenceListItem;
  actions: SequenceRowActionsState;
}

export interface SequenceRowActionsState {
  cloning: boolean;
  handleClone: () => void;
  handleArchiveToggle: () => void;
}

export function useSequenceRowActions(
  seq: SequenceListItem,
  jobId: string,
): SequenceRowActionsState {
  const [cloning, setCloning] = useState(false);
  const openClone = useSequenceWizardStore((s) => s.openClone);
  const queryClient = useQueryClient();
  const archive = useArchiveSequence();
  const unarchive = useUnarchiveSequence();

  const handleClone = () => {
    void (async () => {
      if (cloning) return;
      setCloning(true);
      try {
        const source = await getSequence(jobId, seq.id);
        const prefill = sequenceToWizardPrefill(source);
        openClone({
          jobId,
          sourceId: source.id,
          sourceName: source.name,
          ...prefill,
        });
      } catch (err) {
        showToast.error("Couldn't open the clone wizard. Please try again.", {
          description: getApiErrorMessage(err),
        });
      } finally {
        setCloning(false);
      }
    })();
  };

  const handleArchiveToggle = () => {
    void (async () => {
      try {
        if (seq.is_archived) {
          await unarchive.mutateAsync({ jobId, sequenceId: seq.id });
          showToast.success(`${seq.name} restored`);
        } else {
          await archive.mutateAsync({ jobId, sequenceId: seq.id });
          showToast.success(`${seq.name} archived`);
        }
        queryClient.invalidateQueries({
          queryKey: getListSequencesQueryKey(jobId),
        });
        void invalidateProjectSequences(queryClient, seq.project_id);
      } catch (err) {
        showToast.error(
          seq.is_archived
            ? "Couldn't unarchive the sequence. Please try again."
            : "Couldn't archive the sequence. Please try again.",
          { description: getApiErrorMessage(err) },
        );
      }
    })();
  };

  return { cloning, handleClone, handleArchiveToggle };
}

function renderSequenceRowActionsMenuItems(
  seq: SequenceListItem,
  { cloning, handleClone, handleArchiveToggle }: SequenceRowActionsState,
  { Item, Separator }: ActionMenuKit,
) {
  return (
    <>
      <Item
        onSelect={handleClone}
        disabled={cloning}
        data-testid={`sequence-action-clone-${seq.id}`}
      >
        <Icon name="IconFiles" size={14} />
        Clone sequence
      </Item>
      <Separator />
      <Item
        variant={seq.is_archived ? "default" : "destructive"}
        onSelect={handleArchiveToggle}
        data-testid={`sequence-action-archive-${seq.id}`}
      >
        <Icon name="IconArchive" size={14} />
        {seq.is_archived ? "Unarchive sequence" : "Archive sequence"}
      </Item>
    </>
  );
}

const sequenceActionsDropdownKit: ActionMenuKit = {
  Item: DropdownMenuItem,
  Separator: DropdownMenuSeparator,
};

const sequenceActionsContextKit: ActionMenuKit = {
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
};

export function SequenceActionsContextMenuContent({
  seq,
  actions,
}: {
  seq: SequenceListItem;
  actions: SequenceRowActionsState;
}) {
  return renderSequenceRowActionsMenuItems(
    seq,
    actions,
    sequenceActionsContextKit,
  );
}

export function RowActionsMenu({ seq, actions }: RowActionsMenuProps) {
  const stop = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <div onClick={stop} className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Sequence actions"
            data-testid={`sequence-row-actions-${seq.id}`}
            className="flex size-7 cursor-pointer items-center justify-center rounded-md text-contrast/70 transition-colors hover:bg-white/6 hover:text-contrast data-[state=open]:bg-white/6 data-[state=open]:text-contrast"
          >
            <Icon name="IconDotGrid1x3Vertical" size={15} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={4}>
          {renderSequenceRowActionsMenuItems(
            seq,
            actions,
            sequenceActionsDropdownKit,
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
